import { useContext, useState } from "react";
import Swal from "sweetalert2";
import { trackThenNavigate } from "utils/track";

// react-router-dom components
import { Link } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import BasicLayoutLanding from "layouts/authentication/components/BasicLayoutLanding";

// Images
import bgImage from "assets/images/bg-sign-up-cover.jpeg";

import AuthService from "services/auth-service";
import { AuthContext, useMaterialUIController } from "context";

function Register() {
  const authContext = useContext(AuthContext);

  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  const [inputs, setInputs] = useState({
    name: "",
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({
    nameError: false,
    emailError: false,
    passwordError: false,
  });

  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const changeHandler = (e) => {
    setInputs({
      ...inputs,
      [e.target.name]: e.target.value,
    });
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    const mailFormat = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

    // Reset errors
    setErrors({ nameError: false, emailError: false, passwordError: false });

    // Validations
    if (inputs.name.trim().length === 0) {
      setErrors((prev) => ({ ...prev, nameError: true }));
      return;
    }

    if (inputs.email.trim().length === 0 || !inputs.email.trim().match(mailFormat)) {
      setErrors((prev) => ({ ...prev, emailError: true }));
      return;
    }

    if (!acceptedTerms) {
      Swal.fire("Atenção", "Você precisa aceitar os Termos de Uso e a Política de Privacidade.", "warning");
      return;
    }

    if (inputs.password.trim().length < 8) {
      setErrors((prev) => ({ ...prev, passwordError: true }));
      return;
    }

    setLoading(true);

    const newUser = { name: inputs.name, email: inputs.email, password: inputs.password };

    const myData = {
      data: {
        type: "users",
        attributes: { 
          ...newUser, 
          password_confirmation: newUser.password,
        },
        // relationships: {
        //   roles: {
        //     data: [
        //       {
        //         type: "roles",
        //         id: "2", // Alterado para String (padrão JSON:API)
        //       },
        //     ],
        //   },
        // },
      },
    };

    try {
      // 1. Criar o utilizador no banco de dados via Laravel
      const response = await AuthService.register(myData);

      const token = response.access_token;

      // Login automático: mesmo armazenamento do AuthService.login (localStorage + cookie)
      if (token) {
        localStorage.setItem("token", token);

        const isProd = process.env.NODE_ENV === 'production';
        const domainAttr = isProd ? '; domain=.djparafestas.com.br' : ''; 
        const secureAttr = isProd ? '; Secure' : '';
        document.cookie = `dj_auth_token=${token}; path=/${domainAttr}; max-age=86400; samesite=Lax${secureAttr}`;
      }

      // Cadastro é gratuito (freemium): entra direto no painel. Reload completo para o
      // AuthContext ler o token e carregar o /me.
      trackThenNavigate("sign_up", { method: "email" }, "/dashboard");
    } catch (err) {
      setLoading(false);
      const errorMessage = err.response?.data?.message || err.message || "Erro ao realizar cadastro.";
      
      Swal.fire({
        icon: "error",
        title: "Erro no Cadastro",
        text: errorMessage,
      });
      console.error("Erro no fluxo de registro:", err);
    }
  };

  return (
    <BasicLayoutLanding image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor={sidenavColor}
          borderRadius="lg"
          coloredShadow={sidenavColor}
          mx={2}
          mt={-3}
          p={3}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Cadastro
          </MDTypography>
          <MDTypography display="block" variant="button" color="white" my={1}>
            Crie sua conta grátis, sem cartão
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" method="POST" onSubmit={submitHandler}>
            <MDBox mb={2}>
              <MDInput
                type="text"
                label="Nome"
                variant="standard"
                fullWidth
                name="name"
                value={inputs.name}
                onChange={changeHandler}
                error={errors.nameError}
                disabled={loading}
              />
              {errors.nameError && (
                <MDTypography variant="caption" color="error" fontWeight="light">
                  O nome é obrigatório
                </MDTypography>
              )}
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="email"
                label="Email"
                variant="standard"
                fullWidth
                value={inputs.email}
                name="email"
                onChange={changeHandler}
                error={errors.emailError}
                disabled={loading}
              />
              {errors.nameError && (
                <MDTypography variant="caption" color="error" fontWeight="light">
                  O email deve ser válido
                </MDTypography>
              )}
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="password"
                label="Senha"
                variant="standard"
                fullWidth
                name="password"
                value={inputs.password}
                onChange={changeHandler}
                error={errors.passwordError}
                disabled={loading}
              />
              {errors.passwordError && (
                <MDTypography variant="caption" color="error" fontWeight="light">
                  A senha deve ter no mínimo 8 caracteres
                </MDTypography>
              )}
            </MDBox>
            <MDBox display="flex" alignItems="center" ml={-1} mt={2}>
              <Checkbox checked={acceptedTerms} onChange={() => setAcceptedTerms(!acceptedTerms)} />
              <MDTypography
                variant="caption"
                color="text"
                sx={{ cursor: "pointer", userSelect: "none" }}
                onClick={() => setAcceptedTerms(!acceptedTerms)}
              >
                Li e concordo com os{" "}
                <a href="https://djparafestas.com.br/termos-uso" target="_blank" onClick={(e) => e.stopPropagation()} style={{ fontWeight: "bold", color: "inherit" }}>
                  Termos de Uso
                </a>
                {" "}e a{" "}
                <a href="https://djparafestas.com.br/politica-de-privacidade" target="_blank" onClick={(e) => e.stopPropagation()} style={{ fontWeight: "bold", color: "inherit" }}>
                  Política de Privacidade
                </a>.
              </MDTypography>
            </MDBox>
            <MDBox mt={4} mb={1}>
              <MDButton 
                variant="gradient" 
                color={sidenavColor} 
                fullWidth 
                type="submit"
                disabled={loading || !acceptedTerms}
              >
                {loading ? "Processando..." : "Cadastrar e Iniciar Trial"}
              </MDButton>
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                Já tem uma conta?{" "}
                <MDTypography
                  component={Link}
                  to="/auth/login"
                  variant="button"
                  color={sidenavColor}
                  fontWeight="medium"
                  textGradient
                >
                  Entrar
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayoutLanding>
  );
}

export default Register;